import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  type ApiProject,
  type ApiElementGuide,
} from "../lib/api";
import type { ElementCategory, ElementVariant } from "@clarity/shared";
import { ButtonSpinner, PageSpinner } from "../components/Loading";
import { toBreakdanceClipboard } from "../lib/breakdance";

type Load =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "ready"; project: ApiProject; guide: ApiElementGuide | null }
  | { kind: "error" };

type Gen = { busy: boolean; error: string | null };

export function ElementGuidePage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<Load>({ kind: "loading" });
  const [gen, setGen] = useState<Gen>({ busy: false, error: null });
  const [viewMode, setViewMode] = useState<ViewMode>("code");

  const load = useCallback(async () => {
    if (!id) return;
    setState({ kind: "loading" });
    try {
      const proj = await api.getProject(id);
      let guide: ApiElementGuide | null = null;
      try {
        const res = await api.getElementGuide(id);
        guide = res.elementGuide;
      } catch (err) {
        if ((err as { status?: number }).status !== 404) throw err;
      }
      setState({ kind: "ready", project: proj.project, guide });
    } catch (err) {
      const status = (err as { status?: number }).status;
      setState(status === 404 ? { kind: "missing" } : { kind: "error" });
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = useCallback(async () => {
    if (!id || gen.busy) return;
    setGen({ busy: true, error: null });
    try {
      const res = await api.generateElementGuide(id);
      setState((prev) =>
        prev.kind === "ready" ? { ...prev, guide: res.elementGuide } : prev,
      );
      setGen({ busy: false, error: null });
    } catch {
      setGen({ busy: false, error: "Couldn’t generate. Try again." });
    }
  }, [id, gen.busy]);

  if (state.kind === "loading") return <PageSpinner label="Loading element guide" />;
  if (state.kind === "missing") {
    return (
      <Wrap>
        <p className="text-neutral-600">That project doesn’t exist or was archived.</p>
        <Link to="/dashboard" className="mt-3 inline-block text-sm underline">
          Back to dashboard
        </Link>
      </Wrap>
    );
  }
  if (state.kind === "error") {
    return (
      <Wrap>
        <p className="text-neutral-600">Couldn’t load this element guide.</p>
      </Wrap>
    );
  }

  const { project, guide } = state;

  return (
    <section className="mx-auto max-w-5xl px-6 py-10">
      <header className="flex items-start justify-between gap-4 border-b border-neutral-200 pb-6">
        <div>
          <Link
            to={`/projects/${project.id}`}
            className="text-xs text-neutral-500 hover:text-neutral-900"
          >
            ← {project.name}
          </Link>
          <h1 className="mt-2 text-2xl font-semibold">Element guide</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Component patterns observed on{" "}
            <a
              href={project.targetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-neutral-900"
            >
              {project.targetUrl}
            </a>
            {guide ? (
              <>
                {" · "}
                Last generated {formatAgo(guide.runAt)} from{" "}
                {guide.sourcePagesCount} page
                {guide.sourcePagesCount === 1 ? "" : "s"}
              </>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {guide ? (
            <>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
              <button
                type="button"
                onClick={() => void generate()}
                disabled={gen.busy}
                className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-100 disabled:opacity-50"
              >
                {gen.busy ? <ButtonSpinner label="Analyzing…" /> : "Refresh"}
              </button>
            </>
          ) : null}
        </div>
      </header>

      {gen.error ? (
        <p className="mt-4 text-sm text-red-600">{gen.error}</p>
      ) : null}

      {guide ? (
        <GuideContent guide={guide} viewMode={viewMode} />
      ) : (
        <EmptyState busy={gen.busy} onGenerate={generate} />
      )}
    </section>
  );
}

type ViewMode = "code" | "preview";

function ViewModeToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="View mode"
      className="inline-flex rounded-md border border-neutral-300 bg-white p-0.5 text-xs"
    >
      {(["code", "preview"] as const).map((mode) => (
        <button
          key={mode}
          type="button"
          role="tab"
          aria-selected={value === mode}
          onClick={() => onChange(mode)}
          className={
            "rounded px-2.5 py-1 transition " +
            (value === mode
              ? "bg-neutral-900 text-white"
              : "text-neutral-600 hover:text-neutral-900")
          }
        >
          {mode === "code" ? "Code" : "Preview"}
        </button>
      ))}
    </div>
  );
}

function GuideContent({
  guide,
  viewMode,
}: {
  guide: ApiElementGuide;
  viewMode: ViewMode;
}) {
  if (guide.categories.length === 0) {
    return (
      <div className="mt-8 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-6 py-12 text-center">
        <p className="text-sm text-neutral-600">
          We didn’t find any recognizable component patterns. The site may be
          highly dynamic or rely on shadow-root rendering.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-8 space-y-10">
      {viewMode === "preview" && guide.cssHrefs.length === 0 ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          This guide was generated before previews were supported — refresh it
          to capture the site’s stylesheets and unlock the preview view.
        </p>
      ) : null}
      {guide.categories.map((cat) => (
        <CategoryBlock
          key={cat.id}
          category={cat}
          viewMode={viewMode}
          cssHrefs={guide.cssHrefs}
          baseUrl={guide.baseUrl}
        />
      ))}
    </div>
  );
}

function CategoryBlock({
  category,
  viewMode,
  cssHrefs,
  baseUrl,
}: {
  category: ElementCategory;
  viewMode: ViewMode;
  cssHrefs: string[];
  baseUrl: string | null;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? category.variants : category.variants.slice(0, 6);
  return (
    <section>
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">{category.label}</h2>
        <span className="text-xs text-neutral-500">
          {category.variants.length} variant
          {category.variants.length === 1 ? "" : "s"}
        </span>
      </header>
      <div className="space-y-2">
        {visible.map((variant) => (
          <VariantCard
            key={variant.selector}
            variant={variant}
            viewMode={viewMode}
            cssHrefs={cssHrefs}
            baseUrl={baseUrl}
          />
        ))}
      </div>
      {category.variants.length > 6 ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-xs text-neutral-500 hover:text-neutral-900"
        >
          {showAll ? "Show fewer" : `Show all ${category.variants.length}`}
        </button>
      ) : null}
    </section>
  );
}

function VariantCard({
  variant,
  viewMode,
  cssHrefs,
  baseUrl,
}: {
  variant: ElementVariant;
  viewMode: ViewMode;
  cssHrefs: string[];
  baseUrl: string | null;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  async function copyAsBreakdance() {
    try {
      const payload = toBreakdanceClipboard(variant.snippet);
      await navigator.clipboard.writeText(payload);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 1500);
    }
  }

  return (
    <div className="rounded-md border border-neutral-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-3 py-2">
        <div className="min-w-0">
          <code className="block truncate font-mono text-xs text-neutral-900">
            {variant.selector}
          </code>
          <div className="text-[11px] text-neutral-500">
            First seen on {variant.firstPagePath} · {variant.count}{" "}
            occurrence{variant.count === 1 ? "" : "s"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void copyAsBreakdance()}
          title="Copy as Breakdance — paste straight into the Breakdance editor"
          className={
            "shrink-0 rounded-md border px-2 py-1 text-[11px] transition " +
            (copyState === "copied"
              ? "border-emerald-300 bg-emerald-50 text-emerald-700"
              : copyState === "failed"
                ? "border-red-300 bg-red-50 text-red-700"
                : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100")
          }
        >
          {copyState === "copied"
            ? "Copied"
            : copyState === "failed"
              ? "Failed"
              : "Copy as Breakdance"}
        </button>
      </div>
      {viewMode === "code" ? (
        <pre className="overflow-x-auto whitespace-pre-wrap break-words px-3 py-2 font-mono text-[11px] leading-relaxed text-neutral-700">
          {variant.snippet}
        </pre>
      ) : (
        <PreviewFrame
          snippet={variant.snippet}
          cssHrefs={cssHrefs}
          baseUrl={baseUrl}
        />
      )}
    </div>
  );
}

function PreviewFrame({
  snippet,
  cssHrefs,
  baseUrl,
}: {
  snippet: string;
  cssHrefs: string[];
  baseUrl: string | null;
}) {
  const srcDoc = buildPreviewDocument(snippet, cssHrefs, baseUrl);
  return (
    <div className="border-t border-neutral-100 bg-neutral-50">
      <iframe
        title="Element preview"
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        className="block h-64 w-full bg-white"
        loading="lazy"
      />
      <p className="px-3 py-1.5 text-[10px] text-neutral-500">
        Preview is approximate — site CSS is loaded cross-origin and scripts
        are disabled.
      </p>
    </div>
  );
}

function buildPreviewDocument(
  snippet: string,
  cssHrefs: string[],
  baseUrl: string | null,
): string {
  const links = cssHrefs
    .map((href) => `<link rel="stylesheet" href="${escapeAttr(href)}">`)
    .join("");
  const base = baseUrl
    ? `<base href="${escapeAttr(baseUrl)}">`
    : "";
  // Snippets are HTML fragments captured server-side; they're already
  // restricted to known structural tags. The sandbox attribute on the
  // iframe blocks script execution regardless, so even a maliciously-
  // crafted snippet can't run JS in the parent.
  return `<!doctype html>
<html><head><meta charset="utf-8">${base}${links}
<style>
  html, body { margin: 0; padding: 16px; background: transparent; }
  body { overflow: auto; }
</style>
</head><body>${snippet}</body></html>`;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function EmptyState({
  busy,
  onGenerate,
}: {
  busy: boolean;
  onGenerate: () => void;
}) {
  return (
    <div className="mt-12 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-6 py-12 text-center">
      <h2 className="text-base font-semibold">No element guide yet</h2>
      <p className="mt-1 text-sm text-neutral-600">
        Avista will scan every page in this project and group the UI patterns it
        finds — buttons, forms, cards, navigation, footers, and more.
      </p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={busy}
        className="mt-6 rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-60"
      >
        {busy ? <ButtonSpinner label="Analyzing pages…" /> : "Generate element guide"}
      </button>
    </div>
  );
}

// ---------- helpers ----------

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20 text-center">{children}</div>
  );
}

function formatAgo(epochSeconds: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000 - epochSeconds));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86_400) return `${Math.floor(diff / 86_400)}d ago`;
  return new Date(epochSeconds * 1000).toLocaleDateString();
}
